/**
 * Stats Overview Section
 * Quick stats cards showing key metrics
 */

import { Trophy, Target, Coins, TrendingUp } from 'lucide-react';

interface StatsOverviewProps {
  totalXP: number;
  badgesEarned: number;
  questsCompleted: number;
  totalWealth: number;
  achievementsUnlocked: number;
  timelineEvents: number;
}

export function StatsOverviewSection({
  totalXP,
  badgesEarned,
  questsCompleted,
  totalWealth,
  achievementsUnlocked,
  timelineEvents
}: StatsOverviewProps) {
  const stats = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      label: 'Total XP',
      value: totalXP.toLocaleString(),
      color: 'emerald',
      bgGradient: 'from-emerald-900/50 to-teal-900/50',
      borderColor: 'border-emerald-500/30',
      textColor: 'text-emerald-300'
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      label: 'Badges Earned',
      value: badgesEarned,
      color: 'purple',
      bgGradient: 'from-purple-900/50 to-pink-900/50',
      borderColor: 'border-purple-500/30',
      textColor: 'text-purple-300'
    },
    {
      icon: <Target className="w-8 h-8" />,
      label: 'Quests Done',
      value: questsCompleted,
      color: 'blue',
      bgGradient: 'from-blue-900/50 to-cyan-900/50',
      borderColor: 'border-blue-500/30',
      textColor: 'text-blue-300'
    },
    {
      icon: <Coins className="w-8 h-8" />,
      label: 'Total Wealth',
      value: `${totalWealth} 🪙`,
      color: 'gold',
      bgGradient: 'from-yellow-900/50 to-amber-900/50',
      borderColor: 'border-yellow-500/30',
      textColor: 'text-yellow-300'
    }
  ];

  return (
    <div className="bg-gray-900/50 border-2 border-purple/30 rounded-xl p-6">
      <h2 className="text-xl font-cinzel font-bold text-purple mb-4 flex items-center gap-2">
        💼 Your Stats
      </h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br ${stat.bgGradient} border-2 ${stat.borderColor} rounded-lg p-4 transition-all hover:scale-105 hover:shadow-xl`}
          >
            <div className="flex items-center gap-3">
              <div className={`${stat.textColor}`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-sm text-gray-400 font-semibold">{stat.label}</p>
                <p className={`text-2xl font-orbitron font-black ${stat.textColor}`}>
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Stats */}
      <div className="mt-4 pt-4 border-t border-purple/20">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-2xl font-orbitron font-bold text-yellow-400">
              {achievementsUnlocked}
            </p>
            <p className="text-xs text-gray-400 mt-1">Achievements</p>
          </div>
          <div>
            <p className="text-2xl font-orbitron font-bold text-cyan-400">
              {timelineEvents}
            </p>
            <p className="text-xs text-gray-400 mt-1">Timeline Events</p>
          </div>
        </div>
      </div>
    </div>
  );
}
